import { useMemo, useState } from 'react'
import { Add } from '@mui/icons-material'
import { Box, Button } from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import { QueryKey } from '@tanstack/react-query'
import { useAddOrRemoveProjectTagsWithDocChange } from '@/hooks/doc-changes-wrapper-hooks'
import useOverlay from '@/hooks/useOverlay'
import useRelevantWorkflowState from '@/hooks/useRelevantWorkflowState'
import { Document } from '@/types/documents'
import { ProjectTag } from '@/types/project-tags'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import DocProjectTagsList from './DocProjectTagsList'
import TagSelectMenu from './TagSelectMenu'

type EditableDocProjectTagsListProps = {
  document: Document
  documentQueryKey: QueryKey
}

export default function EditableDocProjectTagsList({
  document,
  documentQueryKey,
}: EditableDocProjectTagsListProps) {
  const { project } = useProjectContext()
  const overlay = useOverlay()
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const getRelevantWorkflowState = useRelevantWorkflowState()
  const relevantWorkflowState = getRelevantWorkflowState(document)

  const [searchText, setSearchText] = useState('')

  const { isAuthorized: canEditProject } = useStytchIsAuthorized(
    'pd.project',
    'edit',
  )

  const { addOrRemoveProjectTags } = useAddOrRemoveProjectTagsWithDocChange({
    documentId: document.id,
    workflowId: selectedWorkflow.id,
    workflowStateId: relevantWorkflowState?.id || '',
    detailQueryKey: documentQueryKey,
  })

  const projectTagIdsAddedToDoc = useMemo(
    () => document.project_tags_ids || [],
    [document.project_tags_ids],
  )
  const projectTagsAddedToDoc = useMemo(
    () =>
      project.tags?.filter(({ id }) => projectTagIdsAddedToDoc.includes(id)) ||
      [],
    [project.tags, projectTagIdsAddedToDoc],
  )

  if (project.tags?.length === 0) {
    return null
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'calc(100% - 120px) auto',
        maxWidth: '100%',
        minWidth: 0,
        columnGap: 1,
      }}
    >
      <DocProjectTagsList
        projectTags={projectTagsAddedToDoc}
        collapse
        onDelete={(tag) =>
          addOrRemoveProjectTags([
            { op: 'remove', project_tag_id: tag.id as string },
          ])
        }
      />
      <Button
        startIcon={<Add />}
        variant="text"
        onClick={overlay.open}
        sx={{ flexShrink: 0 }}
      >
        Add Tag
      </Button>

      <TagSelectMenu
        overlay={overlay}
        selectedProjectTags={projectTagsAddedToDoc}
        onTagClick={(tag: ProjectTag) => {
          addOrRemoveProjectTags([
            {
              op: projectTagIdsAddedToDoc.includes(tag.id) ? 'remove' : 'add',
              project_tag_id: tag.id,
            },
          ])
        }}
        searchText={searchText}
        setSearchText={setSearchText}
        document={canEditProject ? document : undefined}
        documentQueryKey={documentQueryKey}
      />
    </Box>
  )
}
